import React, {
  useCallback,
  useMemo,
  useRef,
  Dispatch,
  MutableRefObject,
  Ref,
  SetStateAction,
} from 'react';

import { Bed } from '@travel/icons/service';
import { List } from '@travel/icons/ui';
import { useTranslation } from '@travel/traveler-core/hooks';
import { PROVIDER_LIST_TYPE } from '@travel/traveler-core/types/providerInformation';
import TabButton from '@travel/ui/components/NavigationTab/TabButton';

import styles from './listingTypeSelectorTabList.module.scss';

type Props = {
  /** state to capture current active tab */
  activeTabId: string;

  /** function to set activeTabId state */
  setActiveTabId: Dispatch<SetStateAction<string>>;

  /** ref to capture previously selected tab */
  prevActiveTabIdRef: MutableRefObject<string>;

  /** function to set isAutoOpenDatePicker state */
  setIsAutoOpenDatePicker: Dispatch<SetStateAction<boolean>>;
  innerRef: Ref<HTMLDivElement>;

  /** callback function to be called when tab is changed */
  onChangeTabs: (activeTabId: string) => void;
};

/**
 * tab list wrapper that houses room view and plan view tab buttons
 * is sent as a prop to SearchForm which then renders this inside it
 */
function ListingTypeSelectorTabList(props: Props) {
  const {
    activeTabId,
    setActiveTabId,
    prevActiveTabIdRef,
    setIsAutoOpenDatePicker,
    innerRef,
    onChangeTabs,
  } = props;

  // Labels
  const roomListLabel = useTranslation({ id: 'Provider_Info.Navigation.Room_View' });
  const planListLabel = useTranslation({ id: 'Provider_Info.Navigation.Plan_View' });

  // listing type selector tab titles including icons
  const roomListTitle = useMemo(() => {
    const isRoomListSelected = activeTabId === PROVIDER_LIST_TYPE.ROOM_LIST;

    return (
      <span className={styles.tabListTitleWrapper}>
        <Bed size={16} color={isRoomListSelected ? 'white' : 'cilantro'} /> {roomListLabel}
      </span>
    );
  }, [roomListLabel, activeTabId]);

  const planListTitle = useMemo(() => {
    const isPlanListSelected = activeTabId === PROVIDER_LIST_TYPE.PLAN_LIST;

    return (
      <span className={styles.tabListTitleWrapper}>
        <List size={16} color={isPlanListSelected ? 'white' : 'cilantro'} /> {planListLabel}
      </span>
    );
  }, [planListLabel, activeTabId]);

  const tabs = useMemo(
    () => [
      {
        id: PROVIDER_LIST_TYPE.ROOM_LIST,
        title: roomListTitle,
        content: null,
      },
      {
        id: PROVIDER_LIST_TYPE.PLAN_LIST,
        title: planListTitle,
        content: null,
      },
    ],
    [planListTitle, roomListTitle],
  );

  const handleChangeTabs = useCallback(
    (id: string) => {
      prevActiveTabIdRef.current = activeTabId;
      // to re-calculated the text ellipsis when the specific tab is displayed
      setActiveTabId(id);

      onChangeTabs(id);
    },
    [activeTabId, onChangeTabs, prevActiveTabIdRef, setActiveTabId],
  );

  const onClickTab = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      handleChangeTabs(event.currentTarget.id);
      setIsAutoOpenDatePicker(false);
    },
    [handleChangeTabs, setIsAutoOpenDatePicker],
  );

  const onKeyPressed = useCallback(
    (newTabId?: string) => {
      if (newTabId) {
        handleChangeTabs(newTabId);
        tabRefs.current[newTabId]?.focus();
      }
    },
    [handleChangeTabs],
  );

  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  return (
    <div
      ref={innerRef}
      role="tablist"
      data-testid="listingTypeSelectorTabList-wrapper"
      aria-label=""
      className={styles.tabMenu}
    >
      {tabs.map((tab, index) => {
        const isSelected = activeTabId === tab.id;

        return (
          <TabButton
            key={tab.id}
            isSelected={isSelected}
            tabs={tabs}
            tab={tab}
            selectedTabIndex={index}
            onClickTab={onClickTab}
            onKeyPressed={onKeyPressed}
            tabButtonClassName={styles.tabButton}
            ref={ref => (tabRefs.current[tab.id] = ref)}
          />
        );
      })}
    </div>
  );
}

export default React.memo(ListingTypeSelectorTabList);
